/**
 * LinkFieldPopover
 *
 * Searchable popover for editing Link fields inline in the standard table.
 * Fetches options via frappe.desk.search.search_link with debounced search.
 * Uses Popover + Command (cmdk) pattern matching the rest of the codebase.
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { getERPClient } from '@/lib/erp/client';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { parseLinkSearchResponse, type LinkSearchResult } from '@/lib/erp/utils/linkUtils';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { CellSaveState } from '../data-grid/types';

interface LinkFieldPopoverProps {
  /** Target doctype (e.g., "Item Group") */
  doctype: string;
  /** Current field value */
  value: unknown;
  /** Commit new value (triggers save) */
  onCommit: (newValue: string) => void;
  /** Cancel editing (close without saving) */
  onCancel: () => void;
  /** Cell save state for loading indicator */
  saveState: CellSaveState;
}

export function LinkFieldPopover({
  doctype,
  value,
  onCommit,
  onCancel,
  saveState: _saveState,
}: LinkFieldPopoverProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const committedRef = useRef(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const trimmedDoctype = (doctype || '').trim();
  const debouncedSearch = useDebounce(searchTerm, 300);

  // The search text sent to the API — empty string returns all results (Frappe skips LIKE filter)
  const searchText = debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : '';

  // Auto-open on mount
  useEffect(() => {
    const timer = setTimeout(() => setOpen(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Fetch search results
  const { data: results = [], isLoading } = useQuery<LinkSearchResult[]>({
    queryKey: ['inline-link-search', trimmedDoctype, searchText],
    queryFn: async () => {
      const client = getERPClient();
      const response = await client.call<unknown>(
        'frappe.desk.search.search_link',
        {
          doctype: trimmedDoctype,
          txt: searchText,
          page_length: 20,
        },
      );
      return parseLinkSearchResponse(response, { doctype: trimmedDoctype });
    },
    enabled: open && !!trimmedDoctype,
    staleTime: 30_000,
    gcTime: 60_000,
  });

  const handleSelect = (selectedValue: string) => {
    if (committedRef.current) return;
    committedRef.current = true;
    setOpen(false);

    if (selectedValue !== String(value ?? '')) {
      onCommit(selectedValue);
    } else {
      onCancel();
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen && !committedRef.current) {
      onCancel();
    }
  };

  const currentValue = String(value ?? '');

  return (
    <div ref={anchorRef} className="relative w-full h-full">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className="w-full h-full flex items-center justify-end text-sm truncate [font:inherit] text-inherit">
            {currentValue}
          </div>
        </PopoverAnchor>
        {open && (
          <PopoverContent
            className="!w-[380px] max-w-[92vw] p-0 overflow-hidden border-gray-300 dark:border-gray-700 shadow-lg"
            // Anchor is a full-width, right-aligned value (justify-end), so align
            // the dropdown to the anchor's END (right) edge — under the value the
            // user actually clicks — not its far-left start edge.
            align="end"
            sideOffset={2}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              // Guard: clicking inside the anchor area (e.g. the value display)
              // should NOT dismiss the popover — Radix DismissableLayer treats
              // PopoverAnchor clicks as "outside" and fires onOpenChange(false).
              const target = e.target as Node;
              if (anchorRef.current?.contains(target)) {
                e.preventDefault();
                return;
              }
              if (!committedRef.current) {
                onCancel();
              }
            }}
          >
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search..."
                value={searchTerm}
                onValueChange={setSearchTerm}
                className="h-8 text-sm"
                autoFocus
              />
              <CommandList className="max-h-[216px]">
                {isLoading && (
                  <div className="p-1 space-y-1">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-[36px] rounded-sm bg-muted/50 animate-pulse mx-1"
                      />
                    ))}
                  </div>
                )}
                {!isLoading && results.length === 0 && (
                  <CommandEmpty>No results</CommandEmpty>
                )}
                {!isLoading && results.length > 0 && (
                  <CommandGroup>
                    {results.map((result) => (
                      <CommandItem
                        key={result.value}
                        value={result.value}
                        onSelect={() => handleSelect(result.value)}
                        className="text-sm px-2 py-1.5 min-h-[32px] items-start"
                      >
                        <Check
                          className={cn(
                            'mr-2 mt-0.5 h-3.5 w-3.5 shrink-0',
                            currentValue === result.value ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="whitespace-normal break-words leading-snug">
                          {result.label || result.value}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}
